import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SyncProvider } from './context/SyncContext';
import { ToastProvider } from './components/Toast';
import { LocaleProvider } from './context/LocaleContext';
import { BRAND } from './domain/brand';
import { StudentsProvider, useStudents } from './data/useStudents';
import { LedgerProvider } from './data/useLedger';
import { AttendanceProvider } from './data/useAttendance';
import { OnboardingProvider } from './data/useOnboarding';
import { EventsProvider, useEvents } from './data/useEvents';
import { MakeupProvider } from './data/useMakeup';
import { FamiliesProvider } from './data/useFamilies';
import { InvoicesProvider, useInvoices } from './data/useInvoices';
import { ReportsProvider } from './data/useReports';
import { PracticeProvider } from './data/usePractice';
import { CategoriesProvider } from './data/useCategories';
import { TagsProvider } from './data/useTags';
import { NoteTemplatesProvider } from './data/useNoteTemplates';
import { MessageTemplatesProvider } from './data/useMessageTemplates';
import { FilesProvider } from './data/useFiles';
import { PackagesProvider } from './data/usePackages';
import { PackageTemplatesProvider } from './data/usePackageTemplates';
import { ExpensesProvider } from './data/useExpenses';
import { RepertoireProvider } from './data/useRepertoire';
import { MileageProvider } from './data/useMileage';
import { NotificationsProvider } from './data/useNotifications';
import { SettingsProvider, useSettings, useRecordStudioTimeZone } from './data/useSettings';
import { EntitlementProvider } from './data/useEntitlement';
import { SmsGateProvider } from './data/useSmsGate';
import Paywall from './components/billing/Paywall';
import SignInGate from './components/auth/SignInGate';
import StudioGate from './components/auth/StudioGate';
import PortalApp from './components/portal/PortalApp';
import { portalPath } from './domain/identity';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import CalendarPage from './pages/CalendarPage';
import StudentsPage from './pages/StudentsPage';
import FamiliesPage from './pages/FamiliesPage';
import ReportsPage from './pages/ReportsPage';
import ExpensesPage from './pages/ExpensesPage';
import ExpenseCategoriesPage from './pages/ExpenseCategoriesPage';
import ExpenseImportPage from './pages/ExpenseImportPage';
import MileagePage from './pages/MileagePage';
import PackagesPage from './pages/PackagesPage';
import StudentDetailPage from './pages/StudentDetailPage';
import SettingsPage from './pages/SettingsPage';
import ResourcesPage from './pages/ResourcesPage';
import AccountDeletedPage from './pages/AccountDeletedPage';
import WelcomePage from './pages/WelcomePage';
import SubscribedPage from './pages/SubscribedPage';
import { usePageViews } from './hooks/usePageViews';
import { useThankYouPages } from './hooks/useThankYouPages';
import { signUpAwaitingWelcome, welcomeTarget } from './lib/adConversions';

// Anything not routed below falls through to the catch-all and lands on Home.

function PageSuspenseFallback() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--color-gray-50)',
      color: 'var(--color-gray-500)',
      fontSize: '0.875rem',
    }}>
      Loading…
    </div>
  );
}

function RequireAuth({ children }) {
  const { user, loading, isConfigured } = useAuth();
  if (!isConfigured) return children;
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-gray-50)',
        color: 'var(--color-gray-500)',
        fontSize: '0.875rem',
      }}>
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // No onboarding gate: MMS does not block a new account behind a wizard, so a
  // first-run studio lands on a working Home screen and is prompted, not
  // stopped.
  return children;
}

// A signed-in visitor has no business on the sign-in page and goes into the
// app — to /welcome when the account was made just now in this page (#754,
// src/hooks/useThankYouPages.js), so the two redirects agree.
function PublicOnly({ children }) {
  const { user, loading, isConfigured } = useAuth();
  if (loading) return null;
  if (isConfigured && user) return <Navigate to={signUpAwaitingWelcome() ? welcomeTarget() : '/'} replace />;
  return children;
}

// Milestones are derived from live data rather than stored (domain/onboarding.js),
// so the onboarding provider needs the counts and has to sit inside the providers
// that hold them.
function OnboardingBridge({ children }) {
  const { students, loading: studentsLoading } = useStudents();
  const { eventCount, loading: eventsLoading } = useEvents();
  const { invoiceCount, loading: invoicesLoading } = useInvoices();
  const counts = React.useMemo(
    () => ({ studentCount: students.length, eventCount, invoiceCount }),
    [students.length, eventCount, invoiceCount]
  );
  // An unread list counts zero, and the checklist would tick nothing (#629).
  const countsRead = !studentsLoading && !eventsLoading && !invoicesLoading;
  return <OnboardingProvider counts={counts} countsRead={countsRead}>{children}</OnboardingProvider>;
}

// Currency, date format and mileage units all derive from one locale
// (src/domain/locale.js), as in MMS: it has no currency setting, only a Region
// dropdown that "controls currency, mileage log format, and date/time format".
//
// The locale is a field on the studio record, so LocaleProvider takes it as a
// prop and holds no state; this bridge calls the settings hook inside its
// provider, as OnboardingBridge does. The fallback is silent, so without this
// bridge every studio would render en-US/USD.
//
// While the settings snapshot is in flight `settings.locale` is the default,
// so a non-USD studio can flash dollars for one frame on a cold load; after
// the first load Firestore's local cache serves the document at once.
function LocaleBridge({ children }) {
  const { settings } = useSettings();
  return <LocaleProvider locale={settings.locale}>{children}</LocaleProvider>;
}

// Stores this device's time zone as the studio's when it has none (#620;
// `recordStudioTimeZone` in data/useSettings.jsx says why and when not).
function StudioTimeZoneRecorder() {
  useRecordStudioTimeZone();
  return null;
}

// Where this bundle is served from: /app/ under the dev server, which serves
// the marketing site at the root, and / in a build. vite.config.js bakes it in
// from APP_BASE_PATH; Jest never defines it, so tests run at the root.
const BASENAME = typeof __APP_BASE_PATH__ === 'string' ? __APP_BASE_PATH__ : '/';

// The router's page views for Analytics, which does nothing once the visitor
// has declined (#458, #755, src/lib/analytics.js). A component because the
// hook needs the router above it.
function PageViews() {
  usePageViews();
  return null;
}

// The thank-you pages' routing and their Google Ads conversions (#754). Inside
// AuthProvider for the signed-in user, outside SignInGate so an unconfirmed
// sign-up is still counted where it lands — see the hook.
function ThankYouPages() {
  useThankYouPages();
  return null;
}

export default function App() {
  return (
    <BrowserRouter basename={BASENAME}>
      <PageViews />
      {/* Outermost, so any page or provider below can say something in a
          toast and the toast outlives the navigation it may have caused. */}
      <ToastProvider>
      <AuthProvider>
        <ThankYouPages />
        <SyncProvider>
          <Routes>
          {/* The Family Portal (#772): everything under /portal is its own
              small app beside the teacher's — no SignInGate, StudioGate,
              studio provider or Paywall — so a family never sees the
              teacher's frame and a family's session opens none of a studio's
              listeners. PortalApp has the pages. */}
          <Route path={`${portalPath()}/*`} element={<PortalApp />} />
          <Route
            path="*"
            element={(
          <SignInGate>
          {/* A family's portal login is not a studio (#764): above every
              studio provider, so for that uid none of them mounts — no
              settings seed or studio-zone write, no ensureAccount, no
              Paywall — and it is sent to /portal. Everyone else passes
              straight through on the first render. */}
          <StudioGate>
          {/* Outermost of the data providers: the studio's record decides how
              a name is written (Naming Format) and the locale, which every
              provider below needs. It depends only on the signed-in user. */}
          <SettingsProvider>
          <StudioTimeZoneRecorder />
          <LocaleBridge>
          {/* Inside LocaleBridge, because the Membership tab and the trial
              banner draw a price and a date in the studio's locale. Outside
              every collection provider: it depends only on the signed-in
              user, and the paywall it drives wraps any of them. It writes
              nothing; firestore.rules refuses a client write to both billing
              paths (see data/useEntitlement.jsx). */}
          <EntitlementProvider>
          {/* Whether texts can be sent (#782): one document the server
              writes, read once per session, which every "send a text"
              control waits on. Off until it says otherwise. */}
          <SmsGateProvider>
          <StudentsProvider>
            <LedgerProvider>
              <AttendanceProvider>
                <EventsProvider>
                <MakeupProvider>
                <FamiliesProvider>
                <InvoicesProvider>
                <ExpensesProvider>
                <PackagesProvider>
                <PackageTemplatesProvider>
                <CategoriesProvider>
                <TagsProvider>
                <NoteTemplatesProvider>
                <MessageTemplatesProvider>
                <FilesProvider>
                <PracticeProvider>
                <RepertoireProvider>
                <MileageProvider>
                <ReportsProvider>
                <NotificationsProvider>
                <OnboardingBridge>
                <Suspense fallback={<PageSuspenseFallback />}>
                  <Routes>
                    <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
                    {/* After Delete Account (#432): nobody is signed in by
                        the time it renders, so it sits outside every guard. */}
                    <Route path="/account-deleted" element={<AccountDeletedPage />} />
                    {/* The thank-you pages (#754): where a new account lands,
                        and where Stripe Checkout returns. Signed in, and not
                        behind the Paywall — see each page. */}
                    <Route path="/welcome" element={<RequireAuth><WelcomePage /></RequireAuth>} />
                    <Route path="/subscribed" element={<RequireAuth><SubscribedPage /></RequireAuth>} />
                    {/* /portal and everything under it — the Family Portal,
                        its sign-in and an invitation's join page — is not in
                        this tree: the Routes above StudioGate send it to
                        PortalApp (#772). */}

                    {/* Every signed-in route except /settings sits behind the
                        Paywall, which draws a locked screen for a studio whose
                        access has ended and renders the feature while the
                        record is loading (`undecided` in
                        data/useEntitlement.jsx). firestore.rules is the
                        authority: it refuses the lapsed studio's writes.
                        /settings stays open because Membership, the way back,
                        lives there. */}
                    <Route path="/calendar" element={<RequireAuth><Paywall feature="The calendar"><CalendarPage /></Paywall></RequireAuth>} />
                    <Route path="/students" element={<RequireAuth><Paywall feature="Students"><StudentsPage /></Paywall></RequireAuth>} />
                    <Route path="/students/:id" element={<RequireAuth><Paywall feature="Students"><StudentDetailPage /></Paywall></RequireAuth>} />

                    <Route path="/families" element={<RequireAuth><Paywall feature="Families & Invoicing"><FamiliesPage /></Paywall></RequireAuth>} />
                    <Route path="/invoices" element={<RequireAuth><Paywall feature="Families & Invoicing"><FamiliesPage /></Paywall></RequireAuth>} />
                    <Route path="/expenses" element={<RequireAuth><Paywall feature="Expenses"><ExpensesPage /></Paywall></RequireAuth>} />
                    <Route path="/expenses/categories" element={<RequireAuth><Paywall feature="Expenses"><ExpenseCategoriesPage /></Paywall></RequireAuth>} />
                    <Route path="/expenses/import" element={<RequireAuth><Paywall feature="Expenses"><ExpenseImportPage /></Paywall></RequireAuth>} />
                    <Route path="/mileage" element={<RequireAuth><Paywall feature="Mileage Log"><MileagePage /></Paywall></RequireAuth>} />
                    {/* Packages → Templates (#181), behind the Students
                        entitlement: a package is sold on a student. */}
                    <Route path="/packages" element={<RequireAuth><Paywall feature="Students"><PackagesPage /></Paywall></RequireAuth>} />
                    <Route path="/resources" element={<RequireAuth><Paywall feature="Online Resources"><ResourcesPage /></Paywall></RequireAuth>} />
                    <Route path="/reports" element={<RequireAuth><Paywall feature="Business Reports"><ReportsPage /></Paywall></RequireAuth>} />
                    <Route path="/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />

                    <Route path="/" element={<RequireAuth><Paywall feature={BRAND.name}><HomePage /></Paywall></RequireAuth>} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
                </OnboardingBridge>
                </NotificationsProvider>
                </ReportsProvider>
                </MileageProvider>
                </RepertoireProvider>
                </PracticeProvider>
                </FilesProvider>
                </MessageTemplatesProvider>
                </NoteTemplatesProvider>
                </TagsProvider>
                </CategoriesProvider>
                </PackageTemplatesProvider>
                </PackagesProvider>
                </ExpensesProvider>
                </InvoicesProvider>
                </FamiliesProvider>
                </MakeupProvider>
                </EventsProvider>
              </AttendanceProvider>
            </LedgerProvider>
          </StudentsProvider>
          </SmsGateProvider>
          </EntitlementProvider>
          </LocaleBridge>
          </SettingsProvider>
          </StudioGate>
          </SignInGate>
            )}
          />
          </Routes>
        </SyncProvider>
      </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
